import * as React from "react";
import { Page, Card, Table, Alert, Text, Button, Grid, Form } from "tabler-react";
import TradesSiteWrapper from "./TradesSiteWrapper.react";
import RefreshButton from "./RefreshButton.react";
import { getFleetTrades, getStrategyFleet, todayIST, fmtDateTime, fmtCurrency, fmtAvgPnl, pnlClass } from "./api";

class ShadowReportPage extends React.Component {
  state = {
    loading: true,
    error: "",
    catalog: [],
    data: null,
    strategyId: "",
    dateFrom: "",
    dateTo: "",
  };

  componentDidMount() {
    const params = new URLSearchParams(window.location.search);
    const today = todayIST();
    this.setState(
      {
        strategyId: params.get("strategy") || "",
        dateFrom: params.get("date_from") || today,
        dateTo: params.get("date_to") || today,
      },
      this.load
    );
  }

  load = async () => {
    const { strategyId, dateFrom, dateTo } = this.state;
    this.setState({ loading: true, error: "" });
    try {
      const query = { limit: 5000 };
      if (strategyId) query.strategy_id = strategyId;
      if (dateFrom) query.date_from = dateFrom;
      if (dateTo) query.date_to = dateTo;
      const [fleet, trades] = await Promise.all([
        getStrategyFleet().catch(() => null),
        getFleetTrades(query),
      ]);
      this.setState({
        catalog: (fleet && (fleet.strategies || fleet.rows)) || [],
        data: trades,
        loading: false,
      });
    } catch (e) {
      this.setState({ error: e.message || "Failed to load shadow trades", loading: false });
    }
  };

  renderSummary(data, rows) {
    if (!data) return null;
    return (
      <Text className="text-muted mb-3">
        Showing <strong>{rows.length}</strong> of {data.filtered_total} matching
        {data.total !== data.filtered_total ? ` (${data.total} in log)` : ""}. Totals: {data.trades} trades,{" "}
        {data.wins} wins, {data.losses} losses, win {Number(data.win_rate || 0).toFixed(1)}%, avg
        win <span className={pnlClass(data.avg_win || 0)}>{fmtAvgPnl(data.avg_win, data.wins)}</span>, avg
        loss <span className={pnlClass(data.avg_loss || 0)}>{fmtAvgPnl(data.avg_loss, data.losses)}</span>, PnL{" "}
        <span className={pnlClass(data.pnl_rupees || 0)}>{fmtCurrency(data.pnl_rupees || 0)}</span>, simulated capital{" "}
        <span className={pnlClass((data.ending_capital || 100000) - (data.starting_capital || 100000))}>
          {fmtCurrency(data.ending_capital || 100000)}
        </span>{" "}
        ({Number(data.capital_return_pct || 0).toFixed(2)}% after costs).{" "}
        Defaults to today; clear dates for the full testing log.
      </Text>
    );
  }

  render() {
    const { loading, error, catalog, data, strategyId, dateFrom, dateTo } = this.state;
    const rows = (data && data.rows) || [];
    const skipRows = (data && data.skip_rows) || [];

    return (
      <TradesSiteWrapper>
        <Page.Content
          title="Shadow Report"
          subTitle="Closed virtual trades with rupee P&L and the same ₹1,00,000 capital projection used by Strategy Fleet."
          options={<RefreshButton onClick={this.load} loading={loading} />}
        >
          <Alert type="info">
            <span data-page-note="1">
              Paper trades only. Nothing on this page is sent to your broker. Compare strategies here before you arm one live.
            </span>
          </Alert>
          {error ? <Alert type="danger">{error}</Alert> : null}
          <Card>
            <Card.Body>
              <Grid.Row>
                <Grid.Col md={4}>
                  <Form.Group label="Strategy">
                    <Form.Select
                      value={strategyId}
                      onChange={(e) => this.setState({ strategyId: e.target.value })}
                    >
                      <option value="">All strategies</option>
                      {catalog.map((s) => (
                        <option key={s.strategy_id} value={s.strategy_id}>
                          {(s.display_name || s.strategy_id) + " (" + s.strategy_id + ")"}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Grid.Col>
                <Grid.Col md={3}>
                  <Form.Group label="From date">
                    <Form.Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => this.setState({ dateFrom: e.target.value })}
                    />
                  </Form.Group>
                </Grid.Col>
                <Grid.Col md={3}>
                  <Form.Group label="To date">
                    <Form.Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => this.setState({ dateTo: e.target.value })}
                    />
                  </Form.Group>
                </Grid.Col>
                <Grid.Col md={2}>
                  <Form.Group label={"\u00a0"}>
                    <Button color="primary" block onClick={this.load} disabled={loading}>
                      Apply filter
                    </Button>
                  </Form.Group>
                </Grid.Col>
                <Grid.Col md={4}>
                  <Form.Group label={"\u00a0"}>
                    <Button.List>
                      <Button
                        color="secondary"
                        outline
                        onClick={() => {
                          const today = todayIST();
                          this.setState({ dateFrom: today, dateTo: today }, this.load);
                        }}
                        disabled={loading}
                      >
                        Today
                      </Button>
                      <Button
                        color="secondary"
                        outline
                        onClick={() => this.setState({ dateFrom: "", dateTo: "" }, this.load)}
                        disabled={loading}
                      >
                        Clear dates
                      </Button>
                    </Button.List>
                  </Form.Group>
                </Grid.Col>
              </Grid.Row>
            </Card.Body>
          </Card>

          {loading ? <Text>Loading shadow report…</Text> : null}
          {!loading && !error ? (
            <React.Fragment>
              {this.renderSummary(data, rows)}
              {skipRows.length > 0 ? (
                <Card className="mb-3">
                  <Card.Header>
                    <Card.Title>Skip reasons</Card.Title>
                  </Card.Header>
                  <Table responsive size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColHeader>Time</Table.ColHeader>
                        <Table.ColHeader>Strategy</Table.ColHeader>
                        <Table.ColHeader>Index</Table.ColHeader>
                        <Table.ColHeader>Reason</Table.ColHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {skipRows.map((r, i) => (
                        <Table.Row key={`skip-${r.strategy_id}-${i}`}>
                          <Table.Col>{fmtDateTime(r.ts)}</Table.Col>
                          <Table.Col>{r.strategy_id}</Table.Col>
                          <Table.Col>{r.index_key}</Table.Col>
                          <Table.Col>{r.reason}</Table.Col>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </Card>
              ) : null}
              {rows.length === 0 ? (
                <Alert type="info">No shadow trades for this filter.</Alert>
              ) : (
                <Card>
                  <Card.Header>
                    <Card.Title>{rows.length} closed shadow trades</Card.Title>
                  </Card.Header>
                  <Table responsive highlightRowOnHover>
                    <Table.Header>
                      <Table.Row>
                        <Table.ColHeader>Exit</Table.ColHeader>
                        <Table.ColHeader>Strategy</Table.ColHeader>
                        <Table.ColHeader>Index</Table.ColHeader>
                        <Table.ColHeader>Leg</Table.ColHeader>
                        <Table.ColHeader>Entry</Table.ColHeader>
                        <Table.ColHeader>Exit px</Table.ColHeader>
                        <Table.ColHeader>P&L (₹)</Table.ColHeader>
                        <Table.ColHeader>Result</Table.ColHeader>
                        <Table.ColHeader>Reason</Table.ColHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {rows.map((r, i) => (
                        <Table.Row key={`${r.strategy_id}-${r.exit_ts}-${i}`}>
                          <Table.Col>{fmtDateTime(r.exit_ts)}</Table.Col>
                          <Table.Col>
                            {r.strategy_id}
                            <div className="text-muted small">{fmtDateTime(r.entry_ts)}</div>
                          </Table.Col>
                          <Table.Col>{r.index_key}</Table.Col>
                          <Table.Col>{r.leg}</Table.Col>
                          <Table.Col>{Number(r.entry_spot || 0).toFixed(2)}</Table.Col>
                          <Table.Col>{Number(r.exit_spot || 0).toFixed(2)}</Table.Col>
                          <Table.Col className={pnlClass(Number(r.pnl_rupees || 0))}>
                            {fmtCurrency(r.pnl_rupees || 0)}
                          </Table.Col>
                          <Table.Col className={r.won ? "text-success" : "text-danger"}>
                            {r.won ? "WIN" : "LOSS"}
                          </Table.Col>
                          <Table.Col>{r.exit_reason || "-"}</Table.Col>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </Card>
              )}
            </React.Fragment>
          ) : null}
        </Page.Content>
      </TradesSiteWrapper>
    );
  }
}

export default ShadowReportPage;
